import { createClient } from "@supabase/supabase-js"

import { normalizeApiErrorBody, toAppError } from "@/lib/errors/normalize"
import {
  AppError,
  createRequestHeaders,
  readRequestIdFromResponse,
} from "@/lib/observability"

const mockMode = import.meta.env.VITE_USE_MOCK_DATA === "true"
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

if (!mockMode && (!supabaseUrl || !supabasePublishableKey)) {
  throw new Error(
    "VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY must be set -- see .env.example. " +
      "(Set VITE_USE_MOCK_DATA=true instead to run against mocked data with no real Supabase project.)"
  )
}

export const supabase = createClient(
  mockMode ? "https://mock.supabase.co" : supabaseUrl,
  mockMode ? "mock-publishable-key" : supabasePublishableKey
)

export async function callEdgeFunction<T>(name: string, body: object): Promise<T> {
  const requestHeaders = createRequestHeaders()
  const { data, error } = await supabase.functions.invoke<T>(name, {
    body,
    headers: requestHeaders,
  })

  if (error) {
    const ctx = (error as { context?: Response }).context
    if (ctx) {
      try {
        const parsed = (await ctx.clone().json()) as Record<string, unknown>
        const headerRequestId = readRequestIdFromResponse(ctx)
        const normalized = normalizeApiErrorBody(parsed)
        throw new AppError(normalized.userMessage, {
          requestId: normalized.requestId ?? headerRequestId ?? requestHeaders["X-Request-Id"],
          code: normalized.code,
          reportable: normalized.reportable,
          cause: error,
        })
      } catch (inner) {
        if (inner instanceof AppError) throw inner
      }
    }

    throw toAppError(error)
  }

  return data as T
}

/** Throw a normalized AppError when Supabase returns an error result. */
export function throwIfSupabaseError<T>(result: { data: T; error: unknown }): T {
  if (result.error) throw toAppError(result.error)
  return result.data
}
